import { HistoryApi, HISTORY_ELEMENTS_LIMIT } from '@/api';
import { OneOfLatestAccomplishments, OneOfGeekHistoryElements, HistoryType } from '@/models/geek-profile';
import { uuidv4 } from '@/utils';
import { Module } from 'vuex';
import { RootState } from './root.store';
import { Mutations } from './mutations';
import { Actions } from './actions';

function initializeActivityHistory(data: OneOfGeekHistoryElements[]) {
  data.forEach((e: OneOfGeekHistoryElements, index: number) => {
    if ([HistoryType.Activity, HistoryType.Opportunity].includes(e.historyType)) {
      data[index] = { ...e, id: uuidv4(), collapsed: false };
    }
  });
}

export interface HistoryState {
  latestAccomplishment: OneOfLatestAccomplishments;
  geekHistory: OneOfGeekHistoryElements[];
  pagination: {
    offset: number,
    totalCount: number,
  };
}

const historyApi = new HistoryApi();

export const HistoryModule: Module<HistoryState, RootState> = {
  state: {
    latestAccomplishment: { } as OneOfLatestAccomplishments,
    geekHistory: [] as OneOfGeekHistoryElements[],
    pagination: {
      offset: 0,
      totalCount: 0,
    },
  },

  mutations: {
    [Mutations.history.setLatestAccomplishment](state, latest: OneOfLatestAccomplishments) {
      state.latestAccomplishment = latest;
    },
    [Mutations.history.setGeekHistory](state, history: OneOfGeekHistoryElements[]) {
      state.geekHistory = history;
    },
    [Mutations.history.setHistoryElementsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.history.resetPagination](state) {
      state.pagination.offset = 0;
      state.pagination.totalCount = 0;
    },
    [Mutations.history.addHistoryElements](state, history: OneOfGeekHistoryElements[]) {
      state.geekHistory.push(...history);
    },
    [Mutations.history.incrementPagination](state) {
      state.pagination.offset += HISTORY_ELEMENTS_LIMIT;
    },
  },

  actions: {
    async [Actions.history.fetchLatestAccomplishment]({ commit }, geekId?: string): Promise<void> {
      const latestAccomplishment = await historyApi.fetchLatestAccomplishment(geekId);
      commit(Mutations.history.setLatestAccomplishment, latestAccomplishment);
    },
    async [Actions.history.fetchGeekHistory]({ commit }, { geekId, seasonId, showOnlyActivities }): Promise<void> {
      commit(Mutations.history.resetPagination);
      const geekHistory = await historyApi.fetchGeekHistory(geekId, seasonId, showOnlyActivities);
      initializeActivityHistory(geekHistory.data);
      commit(Mutations.history.setGeekHistory, geekHistory.data);
      commit(Mutations.history.setHistoryElementsCount, geekHistory.totalCount);
    },
    async [Actions.history.fetchMoreGeekHistory](
      { commit, state },
      { geekId, seasonId, showOnlyActivities },
    ): Promise<void> {
      commit(Mutations.history.incrementPagination);
      const geekHistory = await historyApi.fetchGeekHistory(
        geekId,
        seasonId,
        showOnlyActivities,
        state.pagination.offset,
      );
      initializeActivityHistory(geekHistory.data);
      commit(Mutations.history.addHistoryElements, geekHistory.data);
      commit(Mutations.history.setHistoryElementsCount, geekHistory.totalCount);
    },
    [Actions.history.resetLatestAccomplishment]({commit}) {
      commit(Mutations.history.setLatestAccomplishment, {});
    },
  },
};
